import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ThemeContext, THEME_STORAGE_KEY, type Theme } from '@/lib/theme'

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'dark'
  const fromAttr = document.documentElement.getAttribute('data-theme')
  if (fromAttr === 'dark' || fromAttr === 'light') return fromAttr
  return 'dark'
}

function userHasStoredPreference(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readInitialTheme())

  const applyTheme = useCallback((next: Theme, persist: boolean) => {
    document.documentElement.setAttribute('data-theme', next)
    setThemeState(next)
    if (persist) {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next)
      } catch {
        // ignore (private mode, quota)
      }
    }
  }, [])

  const setTheme = useCallback(
    (next: Theme) => applyTheme(next, true),
    [applyTheme],
  )

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }, [theme, setTheme])

  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const handler = (e: MediaQueryListEvent) => {
      if (userHasStoredPreference()) return
      applyTheme(e.matches ? 'light' : 'dark', false)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [applyTheme])

  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
